import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Params,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs/observable/merge';
import { filter, map, mergeMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthenticationService, GasService, I18nService, Logger } from '@app/core';
import { SocketService } from '@app/core/socket.service';
import { EtherInterfaceService } from '@app/core/ether-interface.service';
import { UserProfileService } from '@app/core/user-profile.service';
import { ModalService } from '@app/shared/modal.service';
import { Subscription } from 'rxjs/Subscription';
import * as compareVersions from 'compare-versions';
import { Http } from '@angular/http';
import { AlertService } from '@app/core/alert.service';
import { DOCUMENT, Location, PopStateEvent } from '@angular/common';
import { JackpotService } from '@app/core/jackpot.service';
import { AppService } from '@app/core/app.service';
import { get, x64hash128 } from 'fingerprintjs2';
import { BalanceService, currencyList, CurrencyType } from '@app/core/balance.service';
import { DepositService } from '@app/core/account/deposit.service';
import { PlayerService } from '@app/player.service';
import { ServerType } from '@app/games/models/server-type.model';
import Socket = SocketIOClient.Socket;

const logger = new Logger(`app.component.ts`);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public loading: boolean = false;
  private _auth$: Subscription;
  private _userFetcher$: Subscription;
  private _userInfo$: Subscription;
  private _userInfoEmail$: Subscription;
  private _depositChecker$: Subscription;
  private _activatedRoute$: Subscription;
  private _socket: Socket;
  private _credentialsSubscription: Subscription;

  private _lastPoppedUrl: string;
  private _yScrollStack: number[] = [];

  private lastUrl: string = null;

  private _usdRatesInterval: any;
  private _canonicalLinkElement: HTMLLinkElement;

  @ViewChild('outlet') private outlet: RouterOutlet;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private auth: AuthenticationService,
    private socket: SocketService,
    private etherInterface: EtherInterfaceService,
    private profile: UserProfileService,
    private modalService: ModalService,
    private http: Http,
    private gas: GasService,
    private alertService: AlertService,
    private jackpotService: JackpotService,
    private renderer: Renderer2,
    private appService: AppService,
    private location: Location,
    private balance: BalanceService,
    private depositService: DepositService,
    private player: PlayerService,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document
  ) {
    this.initAuth();
    this.loading = true;
    if ((<any>window).requestIdleCallback) {
      (<any>window).requestIdleCallback(() => {
        this.parseFingerprint();
      });
    } else {
      setTimeout(() => {
        this.parseFingerprint();
      }, 500);
    }
  }

  private parseFingerprint(): void {
    try {
      get(components => {
        const values = components.map(component => component.value);
        const fingerprint = x64hash128(values.join(''), 31);
        logger.info(`Browser fingerprint is generated: ${fingerprint}.`);
        localStorage.setItem('x-fingerprint', fingerprint);
      });
    } catch (e) {
      logger.error(e);
    }
  }

  public hitTracker(trackerId: string): any {
    this.auth.setTrackerCode(trackerId);
    return this.http
      .post('/api/affiliate/tracker', {
        trackingId: trackerId,
        // referralUrl: req.body.referralUrl,
        fingerprint: localStorage.getItem('x-fingerprint'),
        // utm: req.body.utm,
        // country: req.body.country,
      })
      .toPromise();
  }

  public emailConfirmation(code: string): void {
    logger.info('email confirmation token', code);
    if (this.auth.hasCredentials()) {
      try {
        this._userInfoEmail$ = this.auth.userInfo$.subscribe(
          (user: any) => {
            if (user && !user.emailConfirmed) {
              this.router.navigate(['/', { outlets: { modal: 'signup-verify' } }], { queryParamsHandling: 'merge' });
              if (this._userInfoEmail$) {
                this._userInfoEmail$.unsubscribe();
              }
            }
          },
          () => {
            this.makeEmailConfirmation(code);
          }
        );
      } catch (e) {
        if (this._userInfoEmail$) {
          this._userInfoEmail$.unsubscribe();
        }
      }
    } else {
      this.makeEmailConfirmation(code);
    }
  }

  public passwordRecovery(code: string): void {
    logger.info('password recovery token token', code);
    this.router.navigate(['/', { outlets: { modal: 'reset-password' } }], { queryParamsHandling: 'merge' });
  }

  public confirmWithdrawal(token: string, request: string): void {
    logger.info('confirm withdrawal', token, request);
    this.router.navigate(['/', { outlets: { modal: 'confirm-withdrawal' } }], { queryParamsHandling: 'merge' });
  }

  private makeEmailConfirmation(code: string): void {
    this.auth.confirmEmail(code).subscribe(
      (data: any) => {
        this.alertService.error({
          title: 'Email confirmed!',
          text: 'Your email was successfully confirmed!',
          timer: 60,
        });
        this.router.navigate(['/', { outlets: { modal: 'login' } }]);
      },
      e => {
        this.alertService.error({
          title: 'Confirmation failed!',
          text: e.json().message,
          timer: 60,
        });
      }
    );
  }

  modalRouterEvent($event: any): void {
    try {
      if (this.outlet.isActivated) {
        this.renderer.addClass(document.getElementsByTagName('html')[0], 'modal-open');
      } else {
        this.renderer.removeClass(document.getElementsByTagName('html')[0], 'modal-open');
      }
    } catch (e) {
      logger.error(e);
    }
  }

  ngOnInit() {
    this._addCustomScripts();

    this.location.subscribe((ev: PopStateEvent) => {
      this._lastPoppedUrl = ev.url;
    });
    this.router.events.subscribe((ev: any) => {
      if (ev instanceof NavigationStart) {
        if (ev.url !== this._lastPoppedUrl) {
          this._yScrollStack.push(window.scrollY);
        }
      } else if (ev instanceof NavigationEnd) {
        if (ev.url === this._lastPoppedUrl) {
          this._lastPoppedUrl = undefined;
          window.scrollTo(0, this._yScrollStack.pop());
        } else {
          window.scrollTo(0, 0);
        }

        this.checkIfUserLeftGame(ev.urlAfterRedirects);
      }
    });

    this._activatedRoute$ = this.activatedRoute.queryParams.subscribe((params: Params) => {
      if (params.tracker) {
        this.hitTracker(params.tracker);
      }
      if (params.utm_source) {
        this.hitTracker(params.utm_source);
      }

      if (params.emailConfirmationToken) {
        this.emailConfirmation(params.emailConfirmationToken);
      }

      if (params.passwordResetToken) {
        this.passwordRecovery(params.passwordResetToken);
      }

      if (params.confirmWithdrawal && params.request) {
        this.confirmWithdrawal(params.confirmWithdrawal, params.request);
      }
    });

    this.initSocket();

    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    const onNavigationEnd = this.router.events.pipe(filter(event => event instanceof NavigationEnd));

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        mergeMap(route => route.data)
      )
      .subscribe(event => {
        const title = event['title'];
        const customTemplate = event['customTemplate'] || false;
        const allowChat = event['allowChat'] || false;
        this.appService.setFlag('customTemplate', customTemplate);
        this.appService.setFlag('allowChat', allowChat);
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
        this.canonicalLink();
      });

    this.depositService.deposit$.subscribe((event: any) => {
      if (typeof event === 'boolean') {
        return;
      }
      const hasBalance: CurrencyType[] = [];
      for (const currency of currencyList) {
        if (this.balance.getBalance(currency) > 0) {
          hasBalance.push(currency);
        }
      }
      if (!hasBalance.length || (hasBalance.length === 1 && hasBalance.includes(event.data.currency))) {
        this.balance.setCurrency(event.data.currency);
      }
    });

    this.canonicalLink();
  }

  canonicalLink() {
    try {
      let append = false;
      if (!this._canonicalLinkElement) {
        this._canonicalLinkElement = this.doc.createElement('link');
        append = true;
      }
      this._canonicalLinkElement.setAttribute('rel', 'canonical');
      if (append) {
        this.doc.head.appendChild(this._canonicalLinkElement);
      }
      this._canonicalLinkElement.setAttribute('href', this.doc.location.pathname.split('(')[0]);
    } catch (e) {}
  }

  ngAfterViewInit() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loading = true;
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
        this.loading = false;
      }
    });
    this.gas.getPrices();

    this.balance.updateUsdRates();
    if (!this._usdRatesInterval) {
      this._usdRatesInterval = setInterval(() => {
        this.balance.updateUsdRates();
      }, 60000);
    }
  }

  ngOnDestroy() {
    if (this._auth$) {
      this._auth$.unsubscribe();
    }
    if (this._userFetcher$) {
      this._userFetcher$.unsubscribe();
    }
    if (this._userInfo$) {
      this._userInfo$.unsubscribe();
    }
    if (this._userInfoEmail$) {
      this._userInfoEmail$.unsubscribe();
    }
    if (this._depositChecker$) {
      this._depositChecker$.unsubscribe();
    }
    if (this._activatedRoute$) {
      this._activatedRoute$.unsubscribe();
    }
    if (this._credentialsSubscription) {
      this._credentialsSubscription.unsubscribe();
    }
    if (this._usdRatesInterval) {
      clearInterval(this._usdRatesInterval);
    }
  }

  private initAuth(): void {
    this.auth.setReferral(document.referrer);

    // Check if user already signed in (accessToken is valid)
    this._auth$ = this.auth.validate().subscribe(
      () => {
        this._userFetcher$ = this.auth.getUserInfo().subscribe(() => {}, () => {});
      },
      () => {
        this.auth.logout();
      }
    );

    this._userInfo$ = this.auth.userInfo$.subscribe(
      user => {
        this.initUser(user);
      },
      () => {}
    );
  }

  checkIfUserLeftGame(url: string) {
    if (
      this.auth.isAuthenticated() &&
      this.lastUrl &&
      this.lastUrl.startsWith('/games/') &&
      !url.startsWith('/games/') &&
      this.auth.isMainServer()
    ) {
      logger.info('User left game, trying to get user info');
      this.auth.getUserInfo().subscribe(() => {}, () => {});
    }
    this.lastUrl = url;
  }

  private initUser(user: any): void {
    if (user && user._id && user.id) {
      this.initSocket(user.id);

      if (user.balance) {
        this.balance.setBalances(user.balance);

        if (!this.auth.isMainServer() && this.balance.getSavedCurrency() !== CurrencyType.DEMO) {
          if (this.player.getCurrency() !== CurrencyType.DEMO) {
            this.auth.setMode(ServerType.Main);
          }
        } else {
          const hasBalance: CurrencyType[] = [];
          for (const currency of currencyList) {
            if (this.balance.getBalance(currency) > 0) {
              hasBalance.push(currency);
            }
          }

          if (hasBalance.length === 1 && hasBalance[0] !== this.balance.getSavedCurrency()) {
            this.balance.setCurrency(this.balance.getSavedCurrency());
          }
        }
      }
    }
  }

  private initSocket(userId: string = null): void {
    this.socket.leaveGame('/private');
    this.socket.removeListener('/private', 'balance');
    this.socket.removeListener('/private', 'command');
    if (this.auth.credentials) {
      // this.socket.joinGame('/private', this.auth.credentials.idToken);
      this.socket.joinGame('/private', this.auth.credentials.accessToken);
      this.socket
        .listen('/private', 'balance')
        .subscribe((balance: { currency: CurrencyType; amount: number; real: number; bonus: number }) => {
          this.balance.setBalance(balance.currency, {
            real: balance.real,
            bonus: balance.bonus,
          });
        });
      this.socket.listen('/private', 'command').subscribe(event => {
        switch (event.command) {
          case 'refresh-jackpot':
            this.jackpotService.getAuthInfo();
            break;
        }
      });
    }

    if (!this._socket) {
      this._socket = this.socket.joinGame('/');
      if (environment.version !== '(dev)') {
        this._socket.on('min-version', serverVersion => {
          try {
            if (compareVersions(serverVersion, environment.version.split(' ')[0]) > 0) {
              this.modalService
                .openAlert(
                  {
                    hero: 'Client update',
                    title: 'Version mismatch!',
                    text: 'Game server just updated, please refresh this page before using our web client!',
                    ok: 'Refresh page',
                    cancel: 'Ignore',
                  },
                  true
                )
                .result.then(() => {
                  try {
                    caches.open('v1').then(cache => {
                      cache.keys().then(keys => {
                        keys.forEach((request, index, array) => {
                          cache.delete(request);
                        });
                      });
                    });
                  } catch (e) {}
                  window.location.reload(true);
                })
                .catch(e => {});
            }
          } catch (e) {}
        });
      }
    }
  }

  private _addCustomScripts(): void {
    this._setHotjar();
  }

  private _setHotjar(): void {
    if (environment.analytics.hotjarId) {
      const s = this.doc.createElement('script');
      s.type = 'text/javascript';
      s.innerHTML =
        '(function(h,o,t,j,a,r){' +
        '    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};' +
        '    h._hjSettings={hjid:' +
        environment.analytics.hotjarId +
        ',hjsv:6};' +
        "    a=o.getElementsByTagName('head')[0];" +
        "    r=o.createElement('script');r.async=1;" +
        '    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;' +
        '    a.appendChild(r);' +
        "  })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');";
      const head = this.doc.getElementsByTagName('head')[0];
      head.appendChild(s);
    }
  }

  public isImpersonated(): boolean {
    return this.auth.isImpersonated();
  }

  public logout(): void {
    this.auth.logout();
  }

  public get Auth(): any {
    return this.auth.userInfo;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/app.component.ts